import React from 'react';
import { ArrowRight, Award, TrendingUp, Shield } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const Hero = () => {
  const brand = useBrand();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Hero Background - Solid gradient, no image */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#00594C] via-[#00594C] to-[#004A3F]" />

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-8 animate-fadeInUp">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20" data-testid="hero-badge">
              <Award className="w-4 h-4 text-[#99DF63]" />
              <span className="text-sm font-medium">{brand.heroBadge}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight" data-testid="hero-title">
              {brand.heroTitle}{' '}
              {/* {' '} keeps a real space in the text: the block span only breaks the line visually, so without it crawlers read "YourFinancial". */}
              <span className="text-[#99DF63] block">{brand.heroTitleAccent}</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed" data-testid="hero-description">
              {brand.heroSubtitle}
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3" data-testid="hero-feature-1">
                <div className="w-12 h-12 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#99DF63]" />
                </div>
                <div>
                  <div className="font-semibold">Competitive Rates</div>
                  <div className="text-sm text-gray-300">Access to multiple lenders</div>
                </div>
              </div>
              <div className="flex items-center space-x-3" data-testid="hero-feature-2">
                <div className="w-12 h-12 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#99DF63]" />
                </div>
                <div>
                  <div className="font-semibold">Expert Guidance</div>
                  <div className="text-sm text-gray-300">Professional team</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={brand.ctaPrimaryHref}
                className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-105 btn-pulse"
                data-testid="hero-cta-book"
              >
                <span>{brand.ctaPrimary}</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href={brand.ctaSecondaryHref}
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all border border-white/30"
                data-testid="hero-cta-explore"
              >
                <span>{brand.ctaSecondary}</span>
              </a>
            </div>
          </div>

          {/* Right Content - Service Image Grid */}
          <div className="grid grid-cols-2 gap-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <a href="/first-home-buyers" className="group relative overflow-hidden rounded-2xl aspect-square" data-testid="hero-image-home">
              <img
                src="/images/hero/home.jpg"
                alt="Home Loans"
                fetchpriority="high"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-white font-bold text-lg">Home Loans</div>
                <div className="text-gray-300 text-sm">Your dream home awaits</div>
              </div>
            </a>
            <a href="/commercial-loans" className="group relative overflow-hidden rounded-2xl aspect-square" data-testid="hero-image-business" >
              <img
                src="/images/hero/business.jpg"
                alt="Business Loans"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-white font-bold text-lg">Business</div>
                <div className="text-gray-300 text-sm">Grow your enterprise</div>
              </div>
            </a>
            <a href="/car-loans" className="group relative overflow-hidden rounded-2xl aspect-square" data-testid="hero-image-car">
              <img
                src="/images/hero/car.jpg"
                alt="Car Loans"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-white font-bold text-lg">Car Loans</div>
                <div className="text-gray-300 text-sm">Hit the road sooner</div>
              </div>
            </a>
            <a href="/personal-loans" className="group relative overflow-hidden rounded-2xl aspect-square" data-testid="hero-image-personal">
              <img
                src="/images/hero/personal.jpg"
                alt="Personal Loans"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-white font-bold text-lg">Personal</div>
                <div className="text-gray-300 text-sm">Fund your next adventure</div>
              </div>
            </a>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;